// Discord-specific Lobby implementation
// Uses the Worker API instead of direct Firestore access

import { gameAPI, getHeaders } from './api.js';
import { getDiscordInstanceId, getDiscordChannelId, updateActivityState } from './discord.js';
import { flags } from './flags.js';

export class DiscordLobby {
    constructor(playerId, nickname) {
        this.playerId = playerId;
        this.nickname = nickname;
        this.gameCode = null;
        this.isHost = false;
        this.lastStatus = null;
        this.lastGame = null;

        this.playersCallback = null;
        this.gameStartCallback = null;
    }

    // Create a new game
    async createGame() {
        this.isHost = true;

        // Tile bag size is now auto-selected based on player count at game start
        const result = await gameAPI.createGame(this.nickname);
        this.gameCode = result.gameCode;
        this.lastGame = result.game;
        this.lastStatus = result.game.status;

        // Link this Activity instance to the game
        await this.linkInstanceToGame(this.gameCode);

        // Update Discord Activity status
        await updateActivityState('In Lobby', this.gameCode, 1);

        this.startPolling();
        return this.gameCode;
    }

    // Link the Discord Activity instance and channel to a game code
    async linkInstanceToGame(gameCode) {
        const instanceId = getDiscordInstanceId();
        const channelId = getDiscordChannelId();

        const promises = [];
        if (instanceId) {
            promises.push(
                fetch(`/.proxy/api/instances/${encodeURIComponent(instanceId)}/game`, {
                    method: 'POST',
                    headers: getHeaders(),
                    body: JSON.stringify({ gameCode })
                }).catch(e => console.warn('Failed to link instance:', e))
            );
        }
        if (channelId) {
            promises.push(
                fetch(`/.proxy/api/channels/${encodeURIComponent(channelId)}/game`, {
                    method: 'POST',
                    headers: getHeaders(),
                    body: JSON.stringify({ gameCode })
                }).catch(e => console.warn('Failed to link channel:', e))
            );
        }
        await Promise.all(promises);
    }

    // Join an existing game
    async joinGame(gameCode) {
        this.gameCode = gameCode.toUpperCase();

        const result = await gameAPI.joinGame(this.gameCode, this.nickname);
        this.lastGame = result.game;
        this.lastStatus = result.status;
        this.isHost = result.game.hostPlayerId === this.playerId;

        // Link this Activity instance to the game (so others clicking the Activity Card auto-join)
        await this.linkInstanceToGame(this.gameCode);

        // Update Discord Activity status
        const playerCount = result.game.players ? result.game.players.length : 1;
        await updateActivityState('In Lobby', this.gameCode, playerCount);

        this.startPolling();

        // Extract late-join bonus if this player just joined an active game
        let lateJoinBonus;
        if (result.status === 'active' && result.game.players) {
            const me = result.game.players.find(p => p.id === this.playerId);
            if (me?.lateJoinBonus) {
                lateJoinBonus = me.lateJoinBonus;
            }
        }

        return { gameCode: this.gameCode, status: result.status, lateJoinBonus };
    }

    // Start polling for updates
    startPolling() {
        let lastPlayerCount = 0;

        gameAPI.startPolling(this.gameCode, async (game) => {
            this.lastGame = game;

            // Update players list
            if (this.playersCallback) {
                this.playersCallback(game.players);
            }

            // Update Discord Activity if player count changed
            const currentPlayerCount = game.players ? game.players.length : 0;
            if (currentPlayerCount !== lastPlayerCount) {
                lastPlayerCount = currentPlayerCount;
                const state = game.status === 'active' ? 'Playing' : 'In Lobby';
                await updateActivityState(state, this.gameCode, currentPlayerCount);
            }

            // Check if game has started
            if (game.status === 'active' && this.lastStatus !== 'active') {
                this.lastStatus = game.status;
                // Update activity to "Playing"
                await updateActivityState('Playing', this.gameCode, currentPlayerCount);
                if (this.gameStartCallback) {
                    const callback = this.gameStartCallback;
                    this.gameStartCallback = null;
                    callback();
                }
            }

            this.lastStatus = game.status;
        });
    }

    // Start the game (host only)
    async startGame() {
        if (!this.isHost) {
            console.error('Only the host can start the game');
            return;
        }

        await gameAPI.startGame(this.gameCode, flags.all());
    }

    // Leave the lobby
    async leave() {
        if (!this.gameCode) return;

        try {
            await gameAPI.leaveGame(this.gameCode);
        } catch (error) {
            console.error('Error leaving lobby:', error);
        } finally {
            // Stop polling first
            this.cleanup();
            // Then clear the Discord Activity status (don't await, fire and forget)
            updateActivityState(null, null, 0).catch(() => {});
        }
    }

    // Register callback for player list updates
    onPlayersUpdate(callback) {
        this.playersCallback = callback;
    }

    // Register callback for game start
    onGameStart(callback) {
        if (this.lastStatus === 'active') {
            callback();
            return;
        }
        this.gameStartCallback = callback;
    }

    // Get current game state
    getGame() {
        return this.lastGame;
    }

    // Cleanup
    cleanup() {
        gameAPI.stopPolling();
        this.playersCallback = null;
        this.gameStartCallback = null;
        this.lastStatus = null;
        this.lastGame = null;
    }
}
